import { Component, EventEmitter, OnDestroy, Output } from '@angular/core'

import { Store } from '@ngrx/store'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import {
    selectCaseId,
    selectSharedPropertyNamesFromState,
    selectCasePlottingResults,
    selectCaseSegObjectPropertyMap,
    selectCaseSegObjectPropertyMaps,
} from 'src/app/state/case/case.selectors'
import { selectImageLayers } from 'src/app/state/viewer/viewer.selectors'
import { JobApiService } from 'src/app/shared/services/job-api.service'
import {
    ClusteringNormalization,
    JobStatus,
    PipelineType,
    PlottingParameters,
} from 'src/app/shared/model/jobs'
import { closeJobSelectionDialog } from 'src/app/shared/utils/job-selection-dialog-utils'
import { CreateGroupDialogComponent } from './create-group-dialog/create-group-dialog.component'
import { Dialog } from '@angular/cdk/dialog'
import { ToasterService } from 'src/app/shared/services/toaster.service'
import { HttpErrorResponse } from '@angular/common/http'
import { SegObjectPropertyMap } from 'src/app/state/case/case.state'
import { GenericConfirmationDialogModel } from 'src/app/shared/components/generic-confirmation-dialog/generic-confirmation-dialog.model'
import { GenericConfirmationDialogComponent } from 'src/app/shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component'
import { DatasetApiService } from 'src/app/shared/services/dataset-api.service'
import { selectJobs } from 'src/app/state/job/job.selectors'

interface PlotDetail {
    displayedName?: string
    name: string
    option: string[]
    transformValue?: { [key: string]: string | boolean }
}

interface Plot {
    type: string
    details: PlotDetail[]
}

interface PlottingResultsType {
    name: string
    unique_name: string
    image: string
    groups: any[]
    plot_details: any[]
}

@Component({
    selector: 'app-plotting-panel',
    templateUrl: './plotting-panel.component.html',
    styleUrls: ['./plotting-panel.component.scss'],
})
export class PlottingPanelComponent implements OnDestroy {
    ClusteringNormalizationValues = Object.values(ClusteringNormalization)

    plottingResults: PlottingResultsType[] = []
    oldPlottingResults: PlottingResultsType[] = []

    plottingForm: FormGroup
    onGoingPlottingJob: any[] = []

    createJobMode = true
    groups: any[] = []
    readonly caseMarkers$
    readonly plottingResults$
    readonly jobs$
    collapsedResults: string[] = []
    /** Full shared property-name list (may be thousands). Do not *ngFor this. */
    private allProperties: string[] = []
    /** Capped/filtered names for template rendering. */
    properties: string[] = []
    propertySearch = ''
    propertiesHasMore = false
    selectedProperties: string[] = []
    warnings: string[] = []
    private static readonly MAX_PROPERTY_OPTIONS = 50
    readonly SELECTED_PLOT_TYPE_KEY = 'type'
    readonly NEW_EXPERIMENT_NAME_KEY = 'name'
    readonly SELECTED_PROPERTY_KEY = 'property'
    readonly plots: Plot[] = [
        {
            type: 'Swarm',
            details: [
                {
                    displayedName: 'Y-axis scale',
                    name: 'y_axis_scale',
                    option: ['log', 'linear'],
                },
            ],
        },
        {
            type: 'Box',
            details: [
                {
                    displayedName: 'Y-axis scale',
                    name: 'y_axis_scale',
                    option: ['log', 'linear'],
                },
            ],
        },
        {
            type: 'Violin',
            details: [
                {
                    displayedName: 'Y-axis scale',
                    name: 'y_axis_scale',
                    option: ['log', 'linear'],
                },
                {
                    displayedName: 'Density normalization',
                    name: 'density_norm',
                    option: ['area', 'count', 'width'],
                },
                {
                    displayedName: 'Violin interior representation',
                    name: 'inner',
                    option: ['box', 'quartile', 'point'],
                },
            ],
        },
        {
            type: 'Bar',
            details: [
                {
                    displayedName: 'Y-axis scale',
                    name: 'y_axis_scale',
                    option: ['log', 'linear'],
                },
                {
                    displayedName: 'Errorbar method',
                    name: 'errorbar',
                    option: [
                        'confidence interval',
                        'percentile interval',
                        'standard error',
                        'standard deviation',
                    ],
                    transformValue: {
                        'confidence interval': 'ci',
                        'percentile interval': 'pi',
                        'standard error': 'se',
                        'standard deviation': 'sd',
                    },
                },
            ],
        },
        {
            type: 'Histogram',
            details: [
                {
                    displayedName: 'Y-axis scale',
                    name: 'y_axis_scale',
                    option: ['log', 'linear'],
                },
                {
                    displayedName: 'Show KDE',
                    name: 'show_kde',
                    option: ['true', 'false'],
                    transformValue: {
                        true: true,
                        false: false,
                    },
                },
                {
                    displayedName: 'Bin statistics',
                    name: 'bin_statistics',
                    option: ['count', 'frequency', 'probability', 'percent', 'density'],
                },
            ],
        },
        {
            type: 'CDF',
            details: [
                {
                    displayedName: 'Y-axis scale',
                    name: 'y_axis_scale',
                    option: ['log', 'linear'],
                },
                {
                    displayedName: 'Distribution statistics',
                    name: 'dist_statistics',
                    option: ['proportion', 'percent', 'count'],
                },
            ],
        },
        {
            type: 'Heatmap',
            details: [
                {
                    displayedName: 'Color map',
                    name: 'cmap',
                    option: ['gray', 'Reds', 'Blues', 'cividis'],
                },
            ],
        },
    ]

    private readonly destroy$ = new Subject<void>()
    enableJobButton = false
    // private readonly wikiUrl = environment.wikiUrl
    readonly segObjectPropertyMap$
    readonly segObjectPropertyMaps$
    readonly sourceLayers$ = this.store.select(selectImageLayers)
    cohorts: { id: number; name: string }[] = []
    selections: { name: string; type: number }[] = []
    segObjectPropertyMap: SegObjectPropertyMap | undefined

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly store: Store,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService,
        private readonly dialog: Dialog,
        private readonly datasetApi: DatasetApiService
    ) {
        this.segObjectPropertyMap$ = this.store.select(selectCaseSegObjectPropertyMap)
        this.segObjectPropertyMaps$ = this.store.select(selectCaseSegObjectPropertyMaps)
        this.plottingResults$ = this.store.select(selectCasePlottingResults)
        this.plottingResults$.subscribe(results => {
            this.plottingResults = results
        })
        this.jobs$ = this.store.select(selectJobs)
        this.jobs$.subscribe(
            (
                results: {
                    id: number
                    name: string
                    progress: number
                    type: string
                    status: JobStatus
                }[]
            ) => {
                this.oldPlottingResults = this.plottingResults
                this.onGoingPlottingJob = []
                results
                    .filter(
                        job =>
                            job.type === PipelineType.Plotting && job.status !== JobStatus.Rejected
                    )
                    .map(job => {
                        const existingResult = this.plottingResults.find(
                            r => r.plot_details[0].job_id === job.id
                        )

                        if (existingResult) {
                            this.oldPlottingResults = this.oldPlottingResults.filter(
                                r => r !== existingResult
                            )
                            this.onGoingPlottingJob.push(existingResult)
                        } else {
                            // deleted job resultS
                            if (job.status === JobStatus.Closed) {
                                return
                            }
                            const details = parseToJson(job.name)
                            const plotName = details.Plot_Name
                            delete details.Plot_Name
                            delete details.Created_By_Customer
                            this.onGoingPlottingJob.push({
                                name: plotName,
                                progress: job.progress,
                                details: details,
                            })
                        }
                    })

                this.onGoingPlottingJob = [...this.oldPlottingResults, ...this.onGoingPlottingJob]

                function parseToJson(inputStr: string): any {
                    const parts = inputStr
                        .split('.')
                        .map(p => p.trim())
                        .filter(Boolean)

                    const result: Record<string, string> = {}
                    for (const part of parts) {
                        if (part.includes(':')) {
                            let [key, value] = part.split(':', 2)
                            key = key.trim()
                            value = value.trim()
                            result[key] = value
                        }
                    }

                    return result
                }
            }
        )
        this.caseMarkers$ = this.store.select(selectSharedPropertyNamesFromState)
        this.segObjectPropertyMap$.pipe().subscribe(map => {
            this.segObjectPropertyMap = map
        })

        this.caseMarkers$.pipe(takeUntil(this.destroy$)).subscribe(names => {
            this.allProperties = names
            this.refreshDisplayedProperties()
            // Only (re)build the form once; property catalog churn must not recreate controls.
            if (!this.plottingForm) {
                this.initiateForm()
            } else {
                this.syncSelectedPropertyControl()
            }
        })

        this.initiateForm()
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    @Output() paramsSet = new EventEmitter<PlottingParameters>()

    startJob(): void {
        this.enableJobButton = false
        this.jobApi.startPlottingJob(this.getPayload()).subscribe({
            next: () => {
                closeJobSelectionDialog()
                this.initiateForm()
            },
            error: (err: HttpErrorResponse) => {
                this.toaster.httpError(err, 'Error starting job')
            },
        })
    }

    private initiateForm(): void {
        this.buildForm()
        this.groups = []

        this.plottingForm.valueChanges.subscribe(formValue => {
            const hasExperimentName = !!formValue[this.NEW_EXPERIMENT_NAME_KEY]
            const hasSelectedPlotType = !!formValue[this.SELECTED_PLOT_TYPE_KEY]
            const hasSelectedProperty = !!formValue[this.SELECTED_PROPERTY_KEY]

            const selectedPlotType = formValue[this.SELECTED_PLOT_TYPE_KEY]
            const isHeatmap = selectedPlotType === 'Heatmap'
            const hasAtLeastOneGroup = this.groups.length > 0
            this.enableJobButton =
                hasExperimentName &&
                hasSelectedPlotType &&
                (isHeatmap ? this.selectedProperties.length > 0 : hasSelectedProperty) &&
                hasAtLeastOneGroup
        })
    }

    buildForm() {
        const formGroup: any = {
            [this.NEW_EXPERIMENT_NAME_KEY]: new FormControl('', [Validators.required]),
            [this.SELECTED_PLOT_TYPE_KEY]: new FormControl(this.plots[0].type, [
                Validators.required,
            ]),
            [this.SELECTED_PROPERTY_KEY]: new FormControl(
                this.plots[0].type === 'Heatmap' ? [] : this.allProperties[0] || '',
                [Validators.required]
            ),
        }

        const selectedPlot = this.plots.find(p => p.type === this.plots[0].type)
        if (selectedPlot) {
            selectedPlot.details.forEach(detail => {
                formGroup[detail.name] = new FormControl(detail.option ? detail.option[0] : false)
            })
        }

        this.plottingForm = this.formBuilder.group(formGroup)

        if (this.plottingForm) {
            this.plottingForm
                .get(this.SELECTED_PLOT_TYPE_KEY)
                ?.valueChanges.pipe(takeUntil(this.destroy$))
                .subscribe(plotType => {
                    this.updateFormControls(plotType)
                })
        }
    }

    private updateFormControls(plotType: string): void {
        const plot = this.plots.find(p => p.type === plotType)
        if (!plot) return

        const existingControls = Object.keys(this.plottingForm.controls)
        existingControls.forEach(controlName => {
            if (
                controlName !== this.SELECTED_PLOT_TYPE_KEY &&
                controlName !== this.NEW_EXPERIMENT_NAME_KEY &&
                controlName !== this.SELECTED_PROPERTY_KEY
            ) {
                this.plottingForm.removeControl(controlName)
            }
        })

        // Update properties based on plot type
        const isHeatmap = plotType === 'Heatmap'

        if (this.plottingForm.contains(this.SELECTED_PROPERTY_KEY)) {
            this.plottingForm.removeControl(this.SELECTED_PROPERTY_KEY)
        }

        if (isHeatmap) {
            this.plottingForm.addControl(
                this.SELECTED_PROPERTY_KEY,
                new FormControl([], [Validators.required])
            )
            this.selectedProperties = []
        } else {
            this.plottingForm.addControl(
                this.SELECTED_PROPERTY_KEY,
                new FormControl(this.allProperties[0] || '', [Validators.required])
            )
        }

        plot.details.forEach(detail => {
            this.plottingForm.addControl(
                detail.name,
                new FormControl(detail.option ? detail.option[0] : false)
            )
        })

        this.getWarnings()
    }

    openGroupSelectionDialog(): void {
        if (this.segObjectPropertyMap) {
            const dialogRef = this.dialog.open<string>(CreateGroupDialogComponent, {
                autoFocus: 'first-tabbable',
            })

            dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe((group: any) => {
                if (group) {
                    this.groups = [...this.groups, group]
                }
                const formValue = this.plottingForm.value
                const hasExperimentName = !!formValue[this.NEW_EXPERIMENT_NAME_KEY]
                const hasSelectedPlotType = !!formValue[this.SELECTED_PLOT_TYPE_KEY]
                this.enableJobButton =
                    hasExperimentName && hasSelectedPlotType && this.groups.length > 0
                this.getWarnings()
            })
        } else {
            this.toaster.error('Select an intensity map', 'Error')
        }
    }

    getSelectedPlotDetails() {
        const selectedPlotType = this.plottingForm?.get(this.SELECTED_PLOT_TYPE_KEY)?.value
        const selectedPlot = this.plots.find(p => p.type === selectedPlotType)
        return selectedPlot?.details || []
    }

    getNodeIds(nodes: any[]): string {
        return (
            nodes
                ?.map(node => node.label || node.name || node.id)
                .filter(Boolean)
                .join(', ') || ''
        )
    }

    removeGroup(group: any): void {
        if (this.groups.length === 1) {
            this.enableJobButton = false
            this.warnings = []
        }
        this.groups = this.groups.filter(g => g !== group)
    }
    getObjectKeys(obj: any): string[] {
        if (!obj) return []
        return Object.keys(obj).filter(
            key =>
                key !== 'name' &&
                key !== 'Name' &&
                key !== 'type' &&
                key !== 'job_id' &&
                key !== 'image' &&
                key !== 'properties'
        )
    }

    isHeatmapSelected(): boolean {
        const selectedPlotType = this.plottingForm?.get(this.SELECTED_PLOT_TYPE_KEY)?.value
        return selectedPlotType === 'Heatmap'
    }

    openInNewTab(url: string): void {
        window.open(url, '_blank')
    }

    collapseResult(plottingResult: any): void {
        const name = plottingResult.unique_name || plottingResult.name
        if (this.collapsedResults.includes(name)) {
            this.collapsedResults = this.collapsedResults.filter(result => result !== name)
        } else {
            this.collapsedResults = [...this.collapsedResults, name]
        }
    }

    deleteResult(plottingResult: any): void {
        const { name, unique_name } = plottingResult
        const data: GenericConfirmationDialogModel = {
            title: 'Are you sure you want to delete this result?',
            item: name,
            confirmButtonLabel: 'Delete',
            cancelButtonLabel: 'Cancel',
        }

        this.dialog
            .open<string>(GenericConfirmationDialogComponent, {
                data,
                autoFocus: 'dialog',
            })
            .closed.pipe(takeUntil(this.destroy$))
            .subscribe(confirmed => {
                if (!confirmed) return
                this.store
                    .select(selectCaseId)
                    .pipe(take(1))
                    .subscribe(caseId => {
                        this.datasetApi.deletePlottingResult(caseId, unique_name).subscribe({
                            error: error => {
                                this.toaster.httpError(error, 'Failed to delete plotting result')
                            },
                        })
                    })
                this.onGoingPlottingJob = this.onGoingPlottingJob.filter(
                    result => result.unique_name !== unique_name
                )
                this.plottingResults = this.plottingResults.filter(
                    result => result.unique_name !== unique_name
                )
            })
    }

    onPropertySearch(search: string): void {
        this.propertySearch = typeof search === 'string' ? search : ''
        this.refreshDisplayedProperties()
    }

    /**
     * Never bind the full shared catalog to the template — *ngFor over thousands of
     * options freezes the UI when the last large map is deselected (DOM teardown).
     */
    private refreshDisplayedProperties(): void {
        const limit = PlottingPanelComponent.MAX_PROPERTY_OPTIONS
        const trimmed = this.propertySearch.trim().toLowerCase()
        let matched: string[]
        if (!trimmed) {
            matched = this.allProperties
        } else {
            const startsWith: string[] = []
            const contains: string[] = []
            for (const name of this.allProperties) {
                const lower = name.toLowerCase()
                if (lower.startsWith(trimmed)) startsWith.push(name)
                else if (lower.includes(trimmed)) contains.push(name)
            }
            matched = startsWith.length > 0 ? startsWith.concat(contains) : contains
        }

        const selected = this.plottingForm?.get(this.SELECTED_PROPERTY_KEY)?.value
        const selectedName = typeof selected === 'string' ? selected : null
        const head = matched.slice(0, limit)
        if (selectedName && !head.includes(selectedName) && this.allProperties.includes(selectedName)) {
            head.unshift(selectedName)
            if (head.length > limit) head.length = limit
        }
        // Keep selected heatmap properties visible even when filtered out.
        for (const name of this.selectedProperties) {
            if (!head.includes(name) && this.allProperties.includes(name)) {
                head.unshift(name)
            }
        }
        if (head.length > limit) head.length = limit

        this.properties = head
        this.propertiesHasMore = matched.length > limit
    }

    private syncSelectedPropertyControl(): void {
        if (this.isHeatmapSelected()) return
        const control = this.plottingForm?.get(this.SELECTED_PROPERTY_KEY)
        if (!control) return
        const current = control.value
        if (typeof current === 'string' && this.allProperties.includes(current)) return
        control.setValue(this.allProperties[0] || '', { emitEvent: false })
    }

    updateSelectedProperties(property: string): void {
        if (!this.selectedProperties.includes(property)) {
            this.selectedProperties = [...this.selectedProperties, property]
        } else {
            this.selectedProperties = this.selectedProperties.filter(p => p !== property)
        }

        this.plottingForm.get(this.SELECTED_PROPERTY_KEY)?.setValue(this.selectedProperties)
        this.getWarnings()
    }

    getWarnings(): void {
        this.jobApi.checkPlottingJobWarnings(this.getPayload()).subscribe({
            next: (res: any) => {
                this.warnings = res.warnings || []
            },
            error: (err: HttpErrorResponse) => {
                console.error('Error getting warnings: ', err)
            },
        })
    }

    getPayload(): any {
        const formValue = this.plottingForm.value

        if (formValue.type === 'Violin plot' && formValue.inner === 'quartile') {
            formValue.inner = 'quart'
        }

        if (formValue.type === 'Bar plot' && formValue.errorbar) {
            const errorbarMap: { [key: string]: string } = {
                'confidence interval': 'ci',
                'percentile interval': 'pi',
                'standard error': 'se',
                'standard deviation': 'sd',
            }
            formValue.errorbar = errorbarMap[formValue.errorbar]
        }

        if (formValue.type === 'Histogram' && formValue.show_kde) {
            formValue.kde = formValue.show_kde === 'true'
            delete formValue.show_kde
        }

        if (formValue.type === 'Histogram' && formValue.bin_statistics) {
            formValue.stat = formValue.bin_statistics
            delete formValue.bin_statistics
        }
        if (formValue.type === 'CDF' && formValue.dist_statistics) {
            formValue.stat = formValue.dist_statistics
            delete formValue.dist_statistics
        }

        return {
            name: formValue.name,
            type: PipelineType.Plotting,
            plotType: formValue.type,
            groups: this.sanitizeGroupsForJob(this.groups),
            cohorts: this.cohorts,
            properties: formValue.type === 'Heatmap' ? this.selectedProperties : formValue.property,
            details: [formValue],
            segSource: this.segObjectPropertyMap?.name!,
        }
    }

    /**
     * NEN sends selections as [{ id, type, path? }].
     * Plotting nests those same refs under groups[] → Selections → nodes.
     */
    private sanitizeGroupsForJob(groups: any[]): any[] {
        return groups.map(group =>
            (Array.isArray(group) ? group : [group]).map((block: any) => {
                if (block?.id !== 'Selections') return block
                return {
                    id: 'Selections',
                    nodes: (block.nodes || []).map((n: any) => {
                        const { id, type, path } = n
                        return {
                            id,
                            type,
                            ...(Array.isArray(path) && path.length ? { path } : {}),
                        }
                    }),
                }
            })
        )
    }
}
